import { useReq } from "@larner.dev/use-req";
import { Box, Button, Flex, Heading, Image, Spinner, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatCurrency,
  InviteeSparkInvitation,
  SparkInvitationStatus,
  SparkWithRegistrationSummary,
} from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { useHideNav } from "../../../lib/useHideNav";
import { useStoreData } from "../../../lib/store/store";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import { reqSettled } from "../../../lib/reqSettled";
import { toaster } from "../../../lib/toaster";
import { Meta } from "../../../components/Meta/Meta";
import { Avatar } from "../../../components/InviteSparkFlow/InviteSparkPrimitives";
import { formatEventWhen } from "../../../lib/formatEventWhen";
import { primaryProfileImagePath } from "../../../lib/profileMedia";
import { SparkRegisterForm } from "../../../components/SparkRegisterForm/SparkRegisterForm";

// Statuses the invitee can still act on from this page: pending (never
// answered) plus declined/canceled — saying no isn't final; registering
// re-accepts either. Mirrors markAcceptedForRegistration's reopenable set on
// the server. (An answerable invite whose event ended is reported by the API
// as 'expired', so no date check is needed here.)
const canStillRegister = (s: SparkInvitationStatus) =>
  s === "pending" || s === "declined" || s === "canceled";

// A lightweight event summary card. Page-specific (not the picker's EventRow),
// so it lives inline here rather than in the shared primitives.
const EventCard = ({
  title,
  startsAt,
  location,
  image,
  price,
  basePrice,
  onMoreDetails,
}: {
  title: string;
  startsAt: string;
  location?: string;
  image?: string;
  /** Effective price (discounted when a discount is active). 0 = free. */
  price: number;
  /** Pre-discount price; present only when a discount applies (strike-through). */
  basePrice?: number;
  onMoreDetails: () => void;
}) => (
  <Box
    w="100%"
    bg="bg.subtle"
    borderRadius="lg"
    overflow="hidden"
  >
    {image && (
      <Image src={image} alt={title} w="100%" h="9rem" objectFit="cover" />
    )}
    <Box p="1rem">
      <Text textStyle="eyebrow" color="fg.muted" mb="0.375rem">
        Event
      </Text>
      <Flex justify="space-between" align="baseline" gap="0.75rem">
        <Text textStyle="label" color="fg.default">
          {title}
        </Text>
        <Flex align="baseline" gap="0.375rem" flexShrink={0}>
          {basePrice != null && (
            <Text
              textStyle="bodyLight"
              color="fg.muted"
              textDecoration="line-through"
            >
              {formatCurrency(basePrice)}
            </Text>
          )}
          <Text textStyle="label" color="fg.default">
            {price === 0 ? "Free" : formatCurrency(price)}
          </Text>
        </Flex>
      </Flex>
      <Text textStyle="bodyLight" color="fg.muted">
        {formatEventWhen(startsAt)}
      </Text>
      {location && (
        <Text textStyle="bodyLight" color="fg.muted">
          {location}
        </Text>
      )}
      <Button
        mt="0.75rem"
        size="sm"
        variant="outline"
        colorPalette="greenDark"
        onClick={onMoreDetails}
      >
        Get more details
      </Button>
    </Box>
  </Box>
);

const CenteredMessage = ({
  heading,
  body,
}: {
  heading: string;
  body?: string;
}) => (
  <Flex direction="column" align="center" gap="0.5rem" py="1rem">
    <Heading textStyle="pageTitle" color="fg.default" textAlign="center">
      {heading}
    </Heading>
    {body && (
      <Text textStyle="body" color="fg.muted" textAlign="center">
        {body}
      </Text>
    )}
  </Flex>
);

/**
 * Login-gated invite landing page at `/invite/:invitationId` — the destination
 * of the spark-invite email. A logged-out visitor is sent to sign-in and
 * returned here. A logged-in invitee sees who invited them + the event, and
 * registers right on the page (registering accepts the invite server-side).
 *
 * The invitation fetch is scoped to the recipient (the API 404s for anyone who
 * isn't the invitee) and returns only the inviter's pre-event UserProfile — no
 * contact info is exchanged here.
 */
export const SparkInvite = () => {
  useHideNav();
  const navigate = useNavigate();
  const { invitationId } = useParams<{ invitationId: string }>();
  const storeData = useStoreData();
  const loggedIn = isLoggedIn(storeData);

  const [inviteReq, inviteReqState] = useReq<InviteeSparkInvitation>(apiReq);
  const [sparkReq, sparkReqState] =
    useReq<SparkWithRegistrationSummary>(apiReq);
  const [declineReq, declineReqState] = useReq<{ status: string }>(apiReq);
  // Terminal local state after the invitee acts. `registered` covers the
  // register (accept) path; `waitlisted` the wait-list path; `declined` the
  // "can't make it" path.
  const [outcome, setOutcome] = useState<
    "registered" | "waitlisted" | "declined" | null
  >(null);
  // A pending invite first offers a choice (Sign up / Can't make it); only
  // after "Sign up" do we show the register + payment form.
  const [showRegister, setShowRegister] = useState(false);

  const handleDecline = async () => {
    if (!invitationId) return;
    const res = await declineReq.post(
      `/user/spark-invitations/${invitationId}/decline`,
      {},
    );
    // Note: only a real decline succeeds. An INVITATION_NOT_FOUND here means
    // the invite is no longer pending — possibly ACCEPTED in another tab — so
    // showing the "we've let them know" confirmation would be false; surface
    // the error instead. (An already-declined invite never offers this button —
    // the declined view is Sign-up-only below.)
    if (res.success) {
      setOutcome("declined");
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't decline the invite. Please try again.",
      });
    }
  };

  // The spark (with its registration summary) is only needed by the register
  // form, so it's fetched lazily when "Sign up" is clicked — decliners and
  // look-and-leave visitors never pay for it, and first paint doesn't block on
  // a second serial round trip.
  const handleSignUp = () => {
    const ev = inviteReqState.result?.event;
    if (!ev) return;
    setShowRegister(true);
    if (!sparkReqState.result && !sparkReqState.loading) {
      void sparkReq.get(`/spark/${ev.sparkId}`);
    }
  };

  useEffect(() => {
    if (!loggedIn || !invitationId) return;
    void inviteReq.get(`/user/spark-invitations/${invitationId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, invitationId]);

  // Wait for the initial auth load before deciding anything. On a cold load
  // (which the email link always is) the token is hydrated from localStorage
  // synchronously but `user` — and thus isLoggedIn — isn't set until
  // /user/state resolves; redirecting during that window would bounce a
  // logged-in invitee to sign-in.
  if (storeData.loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Logged out → Page.tsx's shared login wall redirects to
  // /sign-in?r=<pathname+search> (isLoggedInPage matches "/invite/"), so we
  // don't hand-roll the redirect here — that dropped the email's utm params and
  // could drift from the shared wall. That wall runs in a useEffect (post
  // paint), so just render the spinner this frame; the redirect fires next.
  if (!loggedIn) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const invitation = inviteReqState.result;

  // Loading the invitation. Gated on "not yet settled" (see reqSettled) rather
  // than `loading`: the fetch only starts in a post-paint useEffect, so on the
  // first frame after auth resolves `loading` is still false and keying off it
  // alone would flash the error branch below before the request fires. The
  // spark is NOT waited on here — it's lazy-loaded by "Sign up".
  if (!reqSettled(inviteReqState)) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // A 404 (not the invitee, or gone) or any other fetch failure.
  if (inviteReqState.error || !invitation) {
    return (
      <Flex justify="center" align="center" minH="60vh" px="1.5rem">
        <CenteredMessage
          heading="This invitation isn't available"
          body="The link may be invalid, or it wasn't meant for this account."
        />
      </Flex>
    );
  }

  const { inviter, event, status } = invitation;
  const eventName = event.title || event.activity;
  const inviterName = inviter.firstName || "Someone";

  // Surface the inviter's identity (name + un-blurred photo) while the invite
  // is still actionable — pending, accepted, or declined/canceled (the invitee
  // can change their mind and register, which re-accepts either). For the
  // truly terminal statuses (revoked, expired — including answerable invites
  // whose event has ended, which the API reports as expired) show a neutral
  // message with no inviter details, mirroring the inbox, which stops
  // surfacing an inviter's profile once the invite is dead. (A revoke stemming
  // from an unmatch/decline never even reaches here: fetchForInvitee's
  // relationship backstop returns null → the 404 branch above.)
  if (!canStillRegister(status) && status !== "accepted") {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="bg.canvas"
        minH="100vh"
        px="1.5rem"
      >
        <CenteredMessage heading="This invitation is no longer available." />
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      align="center"
      bg="bg.canvas"
      minH="100vh"
      px="1.5rem"
      py="2.5rem"
    >
      <Meta>
        <title>{`${inviterName} invited you to ${eventName} | Green Flag`}</title>
      </Meta>
      <Flex direction="column" align="center" gap="1.25rem" w="100%" maxW="30rem">
        <Avatar
          path={primaryProfileImagePath(inviter)}
          alt={inviterName}
          size="96px"
        />
        <Heading textStyle="hero" color="fg.default" textAlign="center">
          {inviterName} invited you to {eventName}
        </Heading>

        <EventCard
          title={eventName}
          startsAt={event.startsAt}
          location={event.location}
          image={event.image}
          price={event.price}
          basePrice={event.basePrice}
          onMoreDetails={() =>
            navigate(`/event/atlanta/${event.sparkId}`, {
              state: { animate: true },
            })
          }
        />

        {outcome === "registered" ? (
          <CenteredMessage heading="You're going — see you there!" />
        ) : outcome === "waitlisted" ? (
          <CenteredMessage
            heading="You're on the wait list"
            body="We'll let you know if a spot opens up."
          />
        ) : outcome === "declined" ? (
          <CenteredMessage
            heading="No worries — we've let them know."
            body={`We told ${inviterName} you can't make it this time.`}
          />
        ) : status === "accepted" ? (
          // Terminal statuses returned early above, so this is an accepted invite.
          <CenteredMessage heading="You're already going to this event 🎉" />
        ) : showRegister ? (
          // "Sign up" chosen → the register + payment form (the spark loads
          // lazily from the click; spinner until it settles).
          sparkReqState.result ? (
            <Box w="100%">
              <SparkRegisterForm
                spark={sparkReqState.result}
                onRegister={() => setOutcome("registered")}
                onJoinWaitlist={() => setOutcome("waitlisted")}
              />
            </Box>
          ) : !reqSettled(sparkReqState) ? (
            <Flex justify="center" py="2rem">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <CenteredMessage
              heading="This event isn't available"
              body="We couldn't load the event details. Please try again later."
            />
          )
        ) : (
          // Actionable, no choice made yet. A fresh (pending) invite offers
          // Sign up / Can't make it. A declined/canceled one offers only
          // Sign up (registering re-accepts it server-side) — there's nothing
          // left to decline, and re-posting a decline would just 404.
          <Flex direction="column" gap="0.75rem" w="100%">
            {status !== "pending" && (
              <Text textStyle="body" color="fg.muted" textAlign="center">
                {status === "declined"
                  ? "You said you couldn't make it — changed your mind?"
                  : "You canceled your registration — changed your mind?"}
              </Text>
            )}
            <Button
              w="100%"
              size="lg"
              colorPalette="greenDark"
              onClick={handleSignUp}
            >
              Sign up
            </Button>
            {status === "pending" && (
              <Button
                w="100%"
                size="lg"
                variant="outline"
                colorPalette="greenDark"
                onClick={handleDecline}
                disabled={declineReqState.loading}
              >
                Can't make it
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
